import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@material-ui/core";
import { SagaService } from "front-end-lib";

import { eStudySaga, eLookupSaga } from "../../../../sagas";
import { eUtilActionTypes } from "../../../../store";
import {
  StudySite,
  ILookup,
  IMddStudyProps,
  IStudyProtocol,
  IAddEditStudySite
} from "../../../../model";
import { isLoading, useSaga, store } from "../../../../utils";
import { useStudySiteProtocols } from "../../../../hooks";
import { BktBreadcrumb, withMddStudy, MddForm } from "../../../../components";
import {
  sortProtocols,
  getBreadcrumbConfig,
  updateProtocolSponsorIds,
  MddStudySiteProtocolCard,
  MddStudySiteProtocolDialog,
  MddStudySiteGeneralCard,
  MddStudySiteApplyIdConfirmDialog,
  MddStudySiteDeleteProtocolConfirmDialog,
  MddStudySiteSaveButton
} from "../shared";

const _MddAddStudySite = ({
  match: {
    params: { id }
  },
  study
}: IMddStudyProps) => {
  const [studySite, setStudySite] = useState<IAddEditStudySite>({
    studyId: id,
    studySiteId: "",
    siteId: "",
    siteName: "",
    sponsorSiteId: "",
    primaryInvestigator: undefined,
    primaryInvestigatorHistory: [],
    protocols: [],
    isActive: true
  });

  // Use useSaga hooks for data fetching
  const { data: sites = [], loading: sitesLoading } = useSaga<ILookup[]>({
    startType: eLookupSaga.FETCH_SITES.toString(),
    successType: eLookupSaga.FETCH_SITES_SUCCESS.toString()
  });

  const { data: studyProtocolsRaw = [], loading: protocolsLoading } = useSaga<
    IStudyProtocol[]
  >({
    startType: eStudySaga.FETCH_STUDY_PROTOCOLS.toString(),
    successType: eStudySaga.FETCH_STUDY_PROTOCOLS_SUCCESS.toString(),
    payload: id
  });

  const studyProtocols = useMemo(() => sortProtocols(studyProtocolsRaw || []), [
    studyProtocolsRaw
  ]);

  // Sync loading states with Redux store for MddForm loading indicator
  useEffect(() => {
    const sitesKey = `undefined_${eLookupSaga.FETCH_SITES}`;
    const protocolsKey = `undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`;

    store.dispatch({
      type: eUtilActionTypes.SET_LOADING,
      payload: { key: sitesKey, loading: sitesLoading }
    });

    store.dispatch({
      type: eUtilActionTypes.SET_LOADING,
      payload: { key: protocolsKey, loading: protocolsLoading }
    });
  }, [sitesLoading, protocolsLoading]);

  // Protocol management hook
  const {
    protocolsAddedByUser,
    hasProtocolValidationErrors,
    deleteConfirmation,
    handleAddProtocols,
    handleProtocolDeleteRequest,
    handleProtocolDeleteConfirm,
    handleProtocolDeleteCancel,
    handleProtocolValidationError
  } = useStudySiteProtocols(studySite, setStudySite);

  const [applyIdToAllProtocols, setApplyIdToAllProtocols] = useState(true);
  const [displayProtocolDialog, setDisplayProtocolDialog] = useState(false);
  const [displayApplyIdConfirmation, setDisplayApplyIdConfirmation] = useState(
    false
  );
  const [sponsorSiteIdHasError, setSponsorSiteIdHasError] = useState(false);

  // Auto-check the "Apply ID to all protocols" checkbox when no protocols exist
  useEffect(() => {
    const protocols = studySite.protocols || [];
    if (protocols.length === 0) {
      setApplyIdToAllProtocols(true);
    }
  }, [studySite.protocols]);

  // Handle main sponsor site ID changes
  const handleStudySiteChange = useCallback(
    (updatedStudySite: IAddEditStudySite) => {
      // If "Apply to all" is checked and sponsor site ID changed, update all protocols
      if (
        applyIdToAllProtocols &&
        updatedStudySite.sponsorSiteId !== studySite.sponsorSiteId
      ) {
        const protocols = updatedStudySite.protocols || [];
        if (protocols.length > 0) {
          const updatedProtocols = protocols.map(protocol => ({
            ...protocol,
            sponsorSiteId: updatedStudySite.sponsorSiteId
          }));

          setStudySite({
            ...updatedStudySite,
            protocols: updatedProtocols
          });
          return;
        }
      }

      // No protocol updates needed, just update the study site
      setStudySite(updatedStudySite);
    },
    [applyIdToAllProtocols, studySite.sponsorSiteId]
  );

  const handleSiteTypeaheadChange = useCallback(
    (selectedOption: ILookup) => {
      const { label, value } = selectedOption || {
        value: undefined,
        label: undefined
      };
      setStudySite({
        ...studySite,
        siteId: value,
        siteName: label
      });
    },
    [studySite]
  );

  const { siteId, sponsorSiteId, primaryInvestigator } = studySite;

  // Helper function to validate protocol sponsor site IDs
  const validateProtocolSponsorSiteIds = useCallback(() => {
    if (!studySite.protocols?.length) {
      return true; // No protocols to validate
    }

    // If "Apply ID to all protocols" is checked, we don't need to validate individual protocol IDs
    if (applyIdToAllProtocols) {
      return true;
    }

    // When "Apply ID to all protocols" is unchecked, each protocol must have its own sponsor site ID
    return studySite.protocols.every(
      protocol =>
        protocol.sponsorSiteId && protocol.sponsorSiteId.trim().length > 0
    );
  }, [studySite.protocols, applyIdToAllProtocols]);

  const disableSaveButton =
    isLoading(
      `undefined_${eLookupSaga.FETCH_SITES}`,
      `undefined_${eStudySaga.SAVE_STUDY_SITE}`,
      `undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`
    ) ||
    !siteId ||
    !sponsorSiteId ||
    !primaryInvestigator ||
    !studySite.protocols?.length ||
    hasProtocolValidationErrors ||
    sponsorSiteIdHasError ||
    !validateProtocolSponsorSiteIds();

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // Convert back to StudySite class for saving, stripping the isExisting flag from protocols
    const protocolsForSave = studySite.protocols.map(protocol => {
      const { isExisting, ...protocolWithoutFlag } = protocol;
      return protocolWithoutFlag;
    });

    // Convert interface data to StudySite class instance for service call
    const studySiteForSave = Object.assign(new StudySite(), {
      StudyId: studySite.studyId,
      StudySiteId: studySite.studySiteId,
      SiteId: studySite.siteId,
      SiteName: studySite.siteName,
      SponsorSiteId: studySite.sponsorSiteId,
      PrimaryInvestigator: studySite.primaryInvestigator,
      IsActive: studySite.isActive,
      PrimaryInvestigatorHistory: studySite.primaryInvestigatorHistory,
      Protocols: protocolsForSave
    });

    SagaService.dispatchSaga({
      type: eStudySaga.SAVE_STUDY_SITE,
      payload: { newStudySite: studySiteForSave, existingStudySite: false }
    });
  };

  const breadcrumbConfig = useMemo(
    () =>
      getBreadcrumbConfig(study.StudyName || "", study.StudyId || "", "add"),
    [study.StudyName, study.StudyId]
  );

  // Apply sponsor site ID updates to all protocols
  const applyProtocolSponsorIdUpdates = useCallback(
    (forceOverwrite: boolean) => {
      const protocols = studySite.protocols || [];

      if (protocols.length === 0) {
        return;
      }

      const updatedProtocols = updateProtocolSponsorIds(
        protocols,
        studySite.sponsorSiteId || "",
        forceOverwrite
      );

      setStudySite({
        ...studySite,
        protocols: updatedProtocols
      });

      // Clear validation errors for all protocols when applying sponsor site ID updates
      protocols.forEach(protocol => {
        handleProtocolValidationError(protocol.id, false);
      });
    },
    [studySite, handleProtocolValidationError]
  );

  const handleApplyIdToAllProtocolsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      const protocols = studySite.protocols || [];

      // If checking the box and there are existing protocols with individual sponsor site IDs
      if (isChecked && protocolsAddedByUser && protocols.length > 0) {
        // Check if any protocol has a different sponsor site ID than the main one
        const hasIndividualIds = protocols.some(
          protocol =>
            protocol.sponsorSiteId &&
            protocol.sponsorSiteId !== studySite.sponsorSiteId
        );

        if (hasIndividualIds) {
          // Show confirmation modal
          setDisplayApplyIdConfirmation(true);
          return; // Don't apply the change yet
        }
      }

      // Update state
      setApplyIdToAllProtocols(isChecked);

      // Only update protocol IDs when CHECKING the box
      // When unchecking, protocols keep their current values
      if (isChecked) {
        applyProtocolSponsorIdUpdates(true);
      }
    },
    [studySite, protocolsAddedByUser, applyProtocolSponsorIdUpdates]
  );

  const confirmApplyIdToAllProtocols = useCallback(() => {
    setApplyIdToAllProtocols(true);
    applyProtocolSponsorIdUpdates(true);
    setDisplayApplyIdConfirmation(false);
  }, [applyProtocolSponsorIdUpdates]);

  const handleCloseApplyIdConfirmation = useCallback(() => {
    setDisplayApplyIdConfirmation(false);
  }, []);

  return (
    <div className="mdd-form--fullheight">
      <BktBreadcrumb
        rootText={breadcrumbConfig.rootText}
        rootUrl={breadcrumbConfig.rootUrl}
        currentLocation={breadcrumbConfig.currentLocation}
        currentNestedLocation={breadcrumbConfig.currentNestedLocation}
        currentNestedLocationUrl={breadcrumbConfig.currentNestedLocationUrl}
      />

      <div className="mdd-study-site--general-info-title">General Info</div>

      <MddForm
        containerClass="mdd-study-site--form-container mdd-study-site--form-container-with-sticky-save"
        keys={[
          `undefined_${eLookupSaga.FETCH_SITES}`,
          `undefined_${eStudySaga.SAVE_STUDY_SITE}`,
          `undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`
        ]}
      >
        <div className="mdd-form--body mdd-form--body-with-sticky-save">
          <MddStudySiteGeneralCard
            studySite={studySite}
            sites={sites || []}
            protocolsAddedByUser={protocolsAddedByUser}
            applyIdToAllProtocols={applyIdToAllProtocols}
            onStudySiteChange={handleStudySiteChange}
            onSiteTypeaheadChange={handleSiteTypeaheadChange}
            onApplyIdToAllProtocolsChange={handleApplyIdToAllProtocolsChange}
            onSponsorSiteIdValidationError={setSponsorSiteIdHasError}
          />

          <div className="mdd-study-site--protocol-section-card">
            <div className="mdd-study-site--protocol-section-header">
              <div className="mdd-study-site--protocol-section-title">
                Study Protocol Participation
              </div>
            </div>

            <div className="mdd-study-site--protocol-section-content">
              <div className="mdd-study-site--protocol-add-button-container">
                <Button
                  className="mdd-study-site--protocol-add-button"
                  size="small"
                  id="btnAddProtocol"
                  disableFocusRipple
                  disableRipple
                  variant="outlined"
                  disabled={
                    !studySite.sponsorSiteId || studyProtocols.length === 0
                  }
                  onClick={() => {
                    setDisplayProtocolDialog(true);
                  }}
                >
                  Add Protocol
                </Button>
              </div>

              {!studySite.sponsorSiteId && studyProtocols.length > 0 && (
                <div className="mdd-study-site--protocol-sponsor-note">
                  *Sponsor Site ID required before adding protocols
                </div>
              )}
              {studyProtocols.length === 0 &&
                !isLoading(`undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`) && (
                  <div className="mdd-study-site--protocol-sponsor-note">
                    * No protocols exist for this study, you must add at least
                    one study protocol in order to add a site
                  </div>
                )}

              {/* Protocol Participation Cards */}
              {studySite.protocols && studySite.protocols.length > 0 && (
                <div className="mdd-study-site--protocol-cards-container">
                  {studySite.protocols.map(protocol => (
                    <MddStudySiteProtocolCard
                      key={`${protocol.id}-${applyIdToAllProtocols}`}
                      protocol={protocol}
                      applyIdToAllProtocols={applyIdToAllProtocols}
                      studySite={studySite}
                      isSiteActive={studySite.isActive}
                      onDelete={handleProtocolDeleteRequest}
                      onStudySiteChange={setStudySite}
                      onValidationError={handleProtocolValidationError}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Save Button Section */}
        <MddStudySiteSaveButton
          onSave={handleSubmit}
          disabled={disableSaveButton}
        />
      </MddForm>

      {/* Add Protocol Dialog */}
      {displayProtocolDialog && (
        <MddStudySiteProtocolDialog
          open
          studyProtocols={studyProtocols}
          existingProtocols={studySite.protocols || []}
          onAddProtocols={handleAddProtocols}
          onClose={() => setDisplayProtocolDialog(false)}
        />
      )}

      {/* Apply ID Confirmation Dialog */}
      {displayApplyIdConfirmation && (
        <MddStudySiteApplyIdConfirmDialog
          open
          onConfirm={confirmApplyIdToAllProtocols}
          onClose={handleCloseApplyIdConfirmation}
        />
      )}

      {/* Delete Protocol Confirmation Dialog */}
      {deleteConfirmation.isOpen && (
        <MddStudySiteDeleteProtocolConfirmDialog
          open
          protocolName={deleteConfirmation.protocolName}
          onConfirm={handleProtocolDeleteConfirm}
          onClose={handleProtocolDeleteCancel}
        />
      )}
    </div>
  );
};

export const MddAddStudySite = withMddStudy()(_MddAddStudySite);
